/**
 * Accessibility Utilities (SRS-WEB-076)
 * Provides keyboard navigation, ARIA labels, and WCAG 2.1 AA compliance helpers
 */

/**
 * Keyboard shortcut handler
 */
export const keyboardShortcuts = {
  // Navigation shortcuts
  NAVIGATE_DASHBOARD: 'd',
  NAVIGATE_MESSAGES: 'm',
  NAVIGATE_PATIENTS: 'p',
  NAVIGATE_SETTINGS: 's',
  NAVIGATE_PROFILE: 'u',
  
  // Action shortcuts
  SEARCH: '/',
  ESCAPE: 'Escape',
  ENTER: 'Enter',
  TAB: 'Tab',
  
  // Help
  HELP: '?',
  SHORTCUTS: 'h'
};

/**
 * Initialize keyboard shortcuts
 */
export const initializeKeyboardShortcuts = (handlers) => {
  const handleKeyDown = (event) => {
    // Don't trigger shortcuts when typing in inputs
    if (
      event.target.tagName === 'INPUT' ||
      event.target.tagName === 'TEXTAREA' ||
      event.target.isContentEditable
    ) {
      return;
    }

    const key = event.key.toLowerCase();
    
    // Handle shortcuts
    if (event.ctrlKey || event.metaKey) {
      // Ctrl/Cmd combinations
      switch (key) {
        case 'k':
          event.preventDefault();
          if (handlers.search) handlers.search();
          break;
        case 's':
          event.preventDefault();
          if (handlers.settings) handlers.settings();
          break;
      }
    } else {
      // Single key shortcuts
      switch (key) {
        case keyboardShortcuts.NAVIGATE_DASHBOARD:
          if (handlers.dashboard) {
            event.preventDefault();
            handlers.dashboard();
          }
          break;
        case keyboardShortcuts.NAVIGATE_MESSAGES:
          if (handlers.messages) {
            event.preventDefault();
            handlers.messages();
          }
          break;
        case keyboardShortcuts.NAVIGATE_PATIENTS:
          if (handlers.patients) {
            event.preventDefault();
            handlers.patients();
          }
          break;
        case keyboardShortcuts.NAVIGATE_SETTINGS:
          if (handlers.settings) {
            event.preventDefault();
            handlers.settings();
          }
          break;
        case keyboardShortcuts.HELP:
          if (handlers.help) {
            event.preventDefault();
            handlers.help();
          }
          break;
      }
    }
  };

  document.addEventListener('keydown', handleKeyDown);
  
  return () => {
    document.removeEventListener('keydown', handleKeyDown);
  };
};

/**
 * Focus management utilities
 */
export const focusManagement = {
  /**
   * Trap focus within a container
   */
  trapFocus: (containerRef) => {
    if (!containerRef.current) return;

    const focusableElements = containerRef.current.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    const handleTabKey = (e) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    };

    containerRef.current.addEventListener('keydown', handleTabKey);
    
    return () => {
      if (containerRef.current) {
        containerRef.current.removeEventListener('keydown', handleTabKey);
      }
    };
  },

  /**
   * Move focus to first focusable element
   */
  focusFirst: (containerRef) => {
    if (!containerRef.current) return;
    
    const focusableElements = containerRef.current.querySelectorAll(
      'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  },

  /**
   * Restore focus to previous element
   */
  restoreFocus: (previousElement) => {
    if (previousElement && previousElement.focus) {
      previousElement.focus();
    }
  }
};

/**
 * ARIA label helpers
 */
export const ariaHelpers = {
  /**
   * Generate ARIA label for navigation items
   */
  navLabel: (itemName, badgeCount = 0) => {
    if (badgeCount > 0) {
      return `${itemName}, ${badgeCount} ${badgeCount === 1 ? 'item' : 'items'} requiring attention`;
    }
    return itemName;
  },

  /**
   * Generate ARIA label for buttons
   */
  buttonLabel: (action, context = '') => {
    return context ? `${action} ${context}` : action;
  },

  /**
   * Generate ARIA live region announcement
   */
  announce: (message, priority = 'polite') => {
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', priority);
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'sr-only';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    
    setTimeout(() => {
      document.body.removeChild(announcement);
    }, 1000);
  }
};

/**
 * Color contrast checker (WCAG 2.1 AA compliance)
 * Minimum contrast ratios: 4.5:1 for normal text, 3:1 for large text
 */
export const checkColorContrast = (foreground, background) => {
  // Convert hex to RGB
  const hexToRgb = (hex) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const getLuminance = (rgb) => {
    const [r, g, b] = [rgb.r, rgb.g, rgb.b].map(val => {
      val = val / 255;
      return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const fgRgb = hexToRgb(foreground);
  const bgRgb = hexToRgb(background);

  if (!fgRgb || !bgRgb) return null;

  const fgLum = getLuminance(fgRgb);
  const bgLum = getLuminance(bgRgb);

  const lighter = Math.max(fgLum, bgLum);
  const darker = Math.min(fgLum, bgLum);

  const contrast = (lighter + 0.05) / (darker + 0.05);

  return {
    ratio: contrast,
    meetsAA: contrast >= 4.5,
    meetsAALarge: contrast >= 3.0,
    level: contrast >= 7 ? 'AAA' : contrast >= 4.5 ? 'AA' : 'Fail'
  };
};

/**
 * Screen reader only class (visually hidden but accessible)
 */
export const srOnlyStyle = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  borderWidth: 0
};

